import React, { useState, useEffect, useCallback } from 'react';
import useForm from '@utils/useForm';
import { styled } from '@linaria/react';
import { useTheme, ITheme } from 'src/theme';
import { useAvivaDispatch, useAvivaSelector } from '@redux/hook';
import { isEmpty, isNil, get } from 'lodash';
import { useHistory } from "react-router-dom";
import { Helpers } from '@utils/helpers';
import { GAAviva } from '@utils/gaAviva';
import { MyInfoForm, MyInfoAddressForm, AddMyInfoForm } from '@models/DPI/MyInfo.model';
import { setIsGlobalError, setLoadingState } from '@redux/slices/loadingSlice';
import { AgentPersonalDetailResponse } from '@models/AgentPersonalDetailRequest.model';
import { QASPostalCodeRequest, QASPostalCodeResponse } from '@models/QASPostalCodeRequest';
import { updatePersonalDetails, updateMyInfoAddressForm, updateMyInfoForm, updateRetrievedMyInfo, updateHasMyInfo, updateMyInfoRawResponse, updateL400, completeStep } from '@redux/slices/DPI/step2Slice';
import { postQASPostalAddress, getAuthoriseURL, getPerson, getRentalProperty, clientDetailCheck, existingLifeHealthCheck, postSavingMyInfo } from '@api/index';
import { MyInfoPersonResponse, AddMyInfoRequest } from '@models/MyInfoRequest.model';

import IconSwap from '@assets/icon/swap.svg';
import SingpassLogo from '@assets/images/singpass-logo.svg';
import { LeftGrid, RightGrid, FullGrid, EmptyGrid, ContainerGrid, Container } from '@components/LayoutGrid';
import ContainerBlock from '@components/ContainerBlock';
import Tag from '@components/Tag';
import Button from '@components/Button';
import MultiSteps from '@components/MultiSteps';
import Heading from '@components/Heading';
import InformationBox from '@components/InformationBox';
import Text from '@components/Text';
import Link from '@components/Link'; 
import ContactBox from '@components/ContactBox';
import Input from '@components/Input';
import Modal from '@components/Modal';
import Table from '@components/Table';
import Dropdown from '@components/Dropdown';
import Listing from '@components/Listing';
import RadioButtonGroup from '@components/RadioButtonGroup';
import RadioButton from '@components/RadioButton';
import Segment from '@components/Segment';
import TextCollapsible from '@components/TextCollapsible';
import Bullet from '@components/Bullet';
import AddInfoModal from './components/AddMyInfo/AddMyInfo';
import { updateTransactionId } from '@redux/slices/DPI/step1Slice';

import { validate, validateAddress, validateAddMyInfoForm } from './service';


const FlexRow = styled.div`
  display: flex;
  justify-content: space-between;
`

const Box = styled.div<{ theme: ITheme }>`
  padding: 30px
  border: 1px solid ${props => props.theme.colors.grey[700]};
  margin-bottom: 30px;
  @media screen and (min-width: 768px) {
    display: flex;
    padding: 30px;
    flex-direction: row-reverse;
    justify-content: space-between;
    align-items: center;
    .singpass-text {
      margin-right: 30px;
    }
  }
`

const PostcodeWrapper = styled.div`
  display: flex;
  flex-direction: column;
  @media screen and (min-width: 768px) {
    flex-direction: row;
    align-items: baseline;
    div {
      width: 100%;
      margin-right: 20px;
    }
    button {
      max-width: 175px;
      white-space: nowrap;
    }
  }
`

const TableWrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`

const Logo = styled.img`
  width: 146px;
  height: 26px;
  margin-bottom: 12px;
`

const injectPersonData = {
  "data": {
    "code": 0,
    "id": 0,
    "person": {
      "uinfin": {
        "lastupdated": "-",
        "source": "0",
        "classification": "-",
        "value": "-"
      },
      "name": {
        "lastupdated": "-",
        "source": "0",
        "classification": "-",
        "value": "-"
      },
      "sex": {
        "lastupdated": "-",
        "code": "-",
        "classification": "-",
        "source": "0",
        "desc": "-"
      },
      "race": {
        "lastupdated": "-",
        "code": "-",
        "classification": "-",
        "source": "0",
        "desc": "-"
      },
      "nationality": {
        "code": "-",
        "desc": "-",
        "classification": "-",
        "source": "0",
        "lastupdated": "-"
      },
      "dob": {
        "lastupdated": "-",
        "source": "0",
        "classification": "-",
        "value": "-"
      },
      "email": {
        "lastupdated": "-",
        "source": "0",
        "classification": "-",
        "value": "-"
      },
      "mobileno": {
        "lastupdated": "-",
        "source": "0",
        "classification": "-",
        "areacode": {
          "value": "-"
        },
        "prefix": {
          "value": "-"
        },
        "nbr": {
          "value": "-"
        }
      },
      "regadd": {
        "lastupdated": "-",
        "source": "0",
        "classification": "-",
        "type": "-",
        "country": {
          "code": "-",
          "desc": "-"
        },
        "unit": {
          "value": "-"
        },
        "street": {
          "value": "-"
        },
        "block": {
          "value": "-"
        },
        "postal": {
          "value": "-"
        },
        "floor": {
          "value": "-"
        },
        "building": {
          "value": "-"
        }
      },
      "marital": {
        "code": "-",
        "desc": "-",
        "classification": "-",
        "source": "0",
        "lastupdated": "-"
      },
      "employment": {
        "lastupdated": "-",
        "source": "-",
        "classification": "-",
        "value": ""
      },
      "residentialstatus": {
        "lastupdated": "-",
        "code": "-",
        "classification": "-",
        "source": "0",
        "desc": "-"
      }
    }
  }
}

const mapAddressInfo = (data: any) => {
  return [
    { text: 'Postal code', value: get(data, 'postal', '-') },
    { text: 'Unit', value: get(data, 'floor', '') ? `${get(data, 'floor', '')}-${get(data, 'unit', '-')}` : get(data, 'unit', '-') },
    { text: 'Street', value: get(data, 'street', '-') },
    { text: 'Block', value: get(data, 'block', '-') },
    { text: 'Building name', value: get(data, 'building', '-') },
  ]
}

const trackOnload = (step: string | number = 0): void => {
  const GA = GAAviva.getInstance();
  let { form } = GA.digitalData;
  const formName = Number(step) === 2 ? 'Mailing address' : 'Personal details';
  form.name = formName;
  GA.trackPageLoad({ form });
}

const trackFormSubmmit = (error: string = ''): void =>  {
  const GA = GAAviva.getInstance();
  if (isEmpty(error)) {
    GA.trackFormSuccess({});
    return;
  }
  const { form } = GA.digitalData;
  form.error.messages = error;
  GA.trackFormFailed({ form });
}


export interface MyInfoProps { }

function MyInfo(props: MyInfoProps) {
  const theme: ITheme = useTheme();
  const history = useHistory();
  const startStep: number = 0, endStep: number = 2;
  const contactInformation = useAvivaSelector(state => state.staticData.contactInformation);
  const salutations = useAvivaSelector(state => state.staticData.salutations);
  const nameGuideline = useAvivaSelector(state => state.staticData.namingGuideline);
  const nationlaities = useAvivaSelector(state => state.staticData.nationlaities);
  const races = useAvivaSelector(state => state.staticData.races);
  const maritalStatus = useAvivaSelector(state => state.staticData.maritalStatus);

  const basicDetails = useAvivaSelector(state => state.dpiStep1.basicDetails);
  const isStep1Completed = useAvivaSelector(state => state.dpiStep1.isCompleted);
  const generalQuestions = useAvivaSelector(state => state.dpiStep1.generalQuestions);
  const transactionId= useAvivaSelector(state=>state.dpiStep1.transactionId);
  const initValue = useAvivaSelector(state => state.dpiStep2.myInfoForm);
  const initAddressValue = useAvivaSelector(state => state.dpiStep2.myInfoAddressForm);
  const l400ProfileSlice = useAvivaSelector(state => state.dpiStep2.l400);
  const personRawResponse = useAvivaSelector(state => state.dpiStep2.myInfoRawResponse);
  const retrievedMyInfo = useAvivaSelector(state => state.dpiStep2.retrievedMyInfo);
  const isShowAddMyInfo = useAvivaSelector(state => state.loading.isShowAddMyInfo);

  const dispatch = useAvivaDispatch();

  const [isPageLoaded, setIsPageLoaded] = useState<boolean>(false);
  const [personalDetails, setPersonalDetails] = useState<any>(null);
  const [addressInformation, setAddressInformation] = useState<any>(null);
  const [step, setStep] = useState<number>(startStep);
  const [isShowGuideline, setShowGuideline] = useState<boolean>(false);
  const [isDifferenceAddress, setIsDifferenceAddress] = useState<number>(isEmpty(initAddressValue) ? 0 : 1);
  const [isShowSwap, setIsShowSwap] = useState<boolean>(false);
  const [isFoundAddress, setIsFoundAddress] = useState<boolean>(false);
  const [familyName, setFamilyName] = useState<any[]>([]);
  const [givenName, setGivenName] = useState<any[]>([]);
  const [fullName, setFulName] = useState<any[]>([]);
  const [isInjected, setInjected] = useState<boolean>(false);
  const [isGenericError, setIsGenericError] = useState<boolean>(false);
  const [isShowMyInfoPopup, setShowMyInfoPopup] = useState<boolean>(false);
  const [isFindAddressValid, setIsFindAddressValid] = useState<boolean>(true);
  const [myInfoRawResponse, setMyInfoRawResponse] = useState<any>(null);
  const [isMyinfoLoaded, setIsMyinfoLoaded] = useState(false);

  const urlParams: URLSearchParams = Helpers.getURLSearchParams();

  const toggleSelectFamilyModal = useCallback(() => { setIsShowSwap(prev => !prev) }, []);

  const toggleSegment = (event: any) => { setIsDifferenceAddress(event.target.value); };

  const toggleGuideline = () => { setShowGuideline(!isShowGuideline); };

  const {
    handleChange,
    values,
    errors,
    formValid,
    patchValue
  } = useForm(new MyInfoForm(initValue), () => { }, validate);

  const {
    handleChange: changeAddressForm,
    values: addressValues,
    errors: addressErrors,
    formValid: addressFormValid,
    patchValue: patchValueAddressForm
  } = useForm(new MyInfoAddressForm(initAddressValue), () => { }, validateAddress);

  const {
    handleChange: changeAddMyInfo,
    values: addMyInfoValues,
    formValid: addMyInfoFormValid,
    errors: addMyInfoError
  } = useForm(new AddMyInfoForm(null), () => { }, validateAddMyInfoForm);


  const gotoNextStep = useCallback(() => {
    const next = step + 1;
    setStep(step + 1);
    //history.push(`/dpi/my-info?step=${next}&rental=true&clientDetail=true&policy=true`);
    history.push(`/dpi/my-info?step=${next}`);
    Helpers.scrollToTop();
  }, [step, setStep, history]);

  const onContinue = async () => {
    dispatch(updateMyInfoForm(values));
    dispatch(updatePersonalDetails(personalDetails));
    dispatch(updateMyInfoAddressForm(isDifferenceAddress ? addressValues : null));

    const data = myInfoRawResponse;
    const person = new MyInfoPersonResponse(data);
    
    if (await saveMyInfo(data, person)) {
      trackOnload(step);
      if (step < endStep) {
        gotoNextStep();
      } else {
        history.push('/dpi/about-you')
      }
    }
  };

  const onBack = useCallback(() => {
    if (step === endStep) {
      const back = step - 1;
      history.push(`/dpi/my-info?step=${back}`);
      setStep(back);
      Helpers.scrollToTop();
    } else {
      history.push('/dpi/coverage')
    }
  }, [step, history])

  const onSwap = useCallback(() => {
    if (isEmpty(fullName)) {
      const full: string[] = values['fullName'].split(' ').map((w: string, idx: number) => ({ w, idx, v: true }));
      values['surname'].split(' ').forEach((e: any) => {
        const refer: any = full.find((o: any) => o.w === e);
        refer.v = false;
      });
      const gName = full.filter((o: any) => o.v === true);
      const fName = full.filter((o: any) => o.v === false);
      setFamilyName(fName);
      setGivenName(gName);
      setFulName(full);
    }

    toggleSelectFamilyModal();
  }, [values, toggleSelectFamilyModal]);
  const onSwapName = (origin: any) => () => {

    if (givenName.length > 1) {
      const nextFamily = [ ...familyName, origin ];
      const nextGivenName = givenName.filter((o: any) => o.idx !== origin.idx);
      const nextFullName = fullName.map((o: any) => o.idx === origin.idx ? { ...origin, v: false } : o);
      console.log(nextFullName);
      setFamilyName(nextFamily);
      setGivenName(nextGivenName); 
      setFulName(nextFullName);
    }
  }

  const onClearTagFamimyName = (text: string, index?: number) => {
    if (!isNil(index)) {
      const origin: any = familyName[index];
      const nextValue = familyName.filter((item, i) => i !== index );
      setFamilyName(nextValue);
      // setGivenName([...givenName, { ...origin, v: true }]);
      const nextFullName = fullName.map((o: any) => o.idx === origin.idx ? { ...origin, v: true } : o);
      const gName = nextFullName.filter((o: any) => o.v === true);
      setGivenName(gName);
      setFulName(nextFullName);
    }
  }

  const onConfirmSwap = () => {
    patchValue({
      givname: givenName.map((o: any) => o.w).join(' '),
      surname: familyName.map((o: any) => o.w).join(' ')
    });
    toggleSelectFamilyModal();
  };

  const onClickRetrieveSingpass = async () => {
    dispatch(updateL400(addMyInfoValues['l400Profile']));
    dispatch(setLoadingState(true));
    try {
      const response = await getAuthoriseURL('dpi');
      const url = get(response, 'data.shortUrl', null);
      console.debug('getAuthoriseURL', url);
      window.open(url, '_self');
    } catch (error) {
      dispatch(setLoadingState(false));
      console.error(error);
    }
  };

  const gotoContact = useCallback((rental: boolean = false) => {
    if (rental) {
      history.push(`/dpi/contact?rental=true`)
    } else {
      history.push('/dpi/contact')
    }
  }, [history]);

  const toggleAddInfoForm = () => {
    setShowMyInfoPopup(!isShowMyInfoPopup);
  }

  const checkBasicDetails = useCallback((retrievedBasicDetails: any) => {
    const savedBasicDetails = basicDetails;
    const dob = get(retrievedBasicDetails, 'dob');
    if ((Helpers.parseDobString(savedBasicDetails?.dob) !== dob) && dob !== '0') {
      console.log(Helpers.parseDobString(savedBasicDetails), retrievedBasicDetails.dob);
      history.push('/dpi/basic-details?doberror=true');
      return false;
    }
    const gender = get(retrievedBasicDetails, 'gender');
    if ((get(savedBasicDetails, 'gender', 'M') !== gender) && gender !== 'U' && gender !== '') {
      history.push('/dpi/basic-details?gendererror=true');
      return false;
    }
    const nationality = get(retrievedBasicDetails, 'nationality');
    if ((get(savedBasicDetails, 'nationalityCode', '') !== nationality) && (nationality !== 'UN' && nationality !== '**' && nationality !== '')) {
      history.push('/dpi/basic-details?nationalityerror=true');
      return false;
    }
    return true;
  }, [basicDetails, history]);

  const checkRentalProperty = useCallback(async (postal: string) => {
    try {
      const rentalResponse = await getRentalProperty(postal);
      const rentalProperty = get(rentalResponse, 'data.rentalProperty');
      const bypassRental = urlParams.get('rental');
      console.debug('checkRentalProperty', rentalResponse, rentalProperty, bypassRental, urlParams);
      if (rentalProperty && bypassRental !== 'true') {
        gotoContact(true);
        return false;
      }
      return true;
    } catch (error) {
      console.error(error);
      setIsGenericError(true);
      return false;
      //return true; //bypass
    }

  }, [urlParams, gotoContact]);

  const checkClientDetails = useCallback(async (personalDetails: any) => {
    const bypassClientDetail = urlParams.get('clientDetail');
    const { nric, nationality, gender, dob } = personalDetails;
    try {
      const clientDetailResponse = await clientDetailCheck(nric, nationality, gender, dob);
      const isClientDetailOk = get(clientDetailResponse, 'data.clientDetailOk', false);
      console.debug('checkClientDetails', bypassClientDetail, isClientDetailOk === false && bypassClientDetail !== 'true', isClientDetailOk);
      if (isClientDetailOk === false && bypassClientDetail !== 'true') {
        setIsGenericError(true);
        return false;
      }
      return true;
    } catch (error) {
      console.error(error);
      setIsGenericError(true);
      return false;
      //return true; //bypass
    }

  }, [urlParams]);

  const checkExistingPolicy = useCallback(async (nirc: string) => {
    const bypassPolicy = urlParams.get('policy');
    try {
      const lifeHealthResponse = await existingLifeHealthCheck(nirc);
      const isPolicyExist = get(lifeHealthResponse, 'data.policyExists');
      console.debug('checkExistingPolicy', isPolicyExist, bypassPolicy);
      if (isPolicyExist === true && bypassPolicy !== 'true') {
        gotoContact(false);
        Helpers.scrollToTop();
        return false;
      }
      return true;
    } catch (error) {
      console.error(error);
      setIsGenericError(true);
      return false;
      // return true; //bypass
    }
  }, [urlParams, gotoContact]);

  const saveMyInfo = useCallback(async (data: any, person: MyInfoPersonResponse, customerId: number = 0) => {
    const { l400Profile } = addMyInfoValues;
    const id = retrievedMyInfo?.id || customerId;
    const postInfoRequest = new AddMyInfoRequest(null);
    postInfoRequest.patchWithPerson(person);
    postInfoRequest.patchWithValues(values);
    postInfoRequest.generalQuestions = generalQuestions || undefined;
    postInfoRequest.myInfoRespData = JSON.stringify(data);
    postInfoRequest.l400Profile = l400Profile ? l400Profile : l400ProfileSlice;
    postInfoRequest.isMailingAddressSame = isDifferenceAddress === 1 ? 'N' : 'Y';
    console.log({ postInfoRequest });
    if (isDifferenceAddress === 1) {
      postInfoRequest.postalCodeMail = addressValues['postcode'];
      postInfoRequest.blockMail = addressValues['block'];
      postInfoRequest.unitMail = addressValues['unit'];
      postInfoRequest.streetNameMail = addressValues['street'];
      postInfoRequest.buildingNameMail = addressValues['building'];
    }
    postInfoRequest.id = id;
    postInfoRequest.transactionId = transactionId;
    try {
      const savedResponse = await postSavingMyInfo(postInfoRequest);
      const persondId = get(savedResponse, 'data', 0);
      person.id = persondId;
      dispatch(updateRetrievedMyInfo(person));
      dispatch(updateHasMyInfo(true));
      trackFormSubmmit();
      return true;
    } catch (error) {
      dispatch(setIsGlobalError(true));
      console.error(error);
      trackFormSubmmit(get(error, 'message'));
      return false;
    }
  }, [addMyInfoValues, dispatch, addressValues, isDifferenceAddress, l400ProfileSlice, retrievedMyInfo, values, generalQuestions]);


  const loadPerson = useCallback(async (res: any, loadNew: boolean = true) => {
    dispatch(updateMyInfoRawResponse(res));
    dispatch(setLoadingState(true));

    const data = get(res, 'data.person');
    const customerId = get(res, 'data.id', 0);
    const person = new MyInfoPersonResponse({ id: customerId, ...data });
    if (customerId) {
      person.id = customerId;
    }
    setMyInfoRawResponse(data);

    const agentInformation = new AgentPersonalDetailResponse(person.personalDetails);
    const address = mapAddressInfo(person.address);
    const myInfoForm = { ...values, ...agentInformation, ...person.address };

    if (loadNew) {
      patchValue(myInfoForm);
    }
    agentInformation.salutation = values['salutation'];
    setPersonalDetails(agentInformation);
    setAddressInformation(address);
    
    if (
      isEmpty(person.personalDetails.dob) ||
      isEmpty(person.personalDetails.fullName) ||
      isEmpty(person.personalDetails.nationality) ||
      isEmpty(person.address.postal)) {
      dispatch(setLoadingState(false));
      if (!isGenericError) {
        setIsGenericError(true);
      }
      return;
    }

    /**
    * 1. Check basic details: dob / gender / nationality 
    * 2. Rental property check
    * 3. Client Check 
    * 4. Existing policy check
    */

    if (!checkBasicDetails(person.personalDetails)) {
      dispatch(setLoadingState(false));
      return;
    }

    if (!await checkClientDetails(person.personalDetails)) {
      dispatch(setLoadingState(false));
      return;
    }

    if (loadNew) {

      if (!await checkRentalProperty(person.address.postal || '')) {
        dispatch(setLoadingState(false));
        return;
      }

      if (!await checkExistingPolicy(person.personalDetails.nric)) {
        dispatch(setLoadingState(false));
        return;
      }


      dispatch(updateRetrievedMyInfo(person));
      dispatch(updateMyInfoForm(new MyInfoForm(myInfoForm)));
      dispatch(updateMyInfoAddressForm(addressValues));
      dispatch(updatePersonalDetails({ ...agentInformation }));

      dispatch(completeStep(true));

      if (await saveMyInfo(data, person, customerId)) {
        gotoNextStep();
      }
    }
    dispatch(setLoadingState(false));
  }, [patchValue, isGenericError, checkBasicDetails, checkClientDetails, checkExistingPolicy, checkRentalProperty, saveMyInfo, gotoNextStep, values, dispatch, addressValues]);

  const getPersonByTransactionId = useCallback(async (transactionId: string) => {
    try {
      const response = await getPerson(transactionId);
      return response;
    } catch (error) {
      console.error(error);
      return false;
    }
  }, []);


  const handleConsent = useCallback(async () => {
    const transaction_id: string = urlParams.get('transaction_id') || '';
    const status = urlParams.get('status') || '';

    if(transaction_id) {
      dispatch(updateTransactionId(transaction_id));
    }
    
    dispatch(setLoadingState(true));

    // No consent params? Do nothing, no error.
    if (!status && !transaction_id) {
      dispatch(setLoadingState(false));
      return;
    }

    // Explicit success path
    if (status === 'consent_accepted' && step === 0 && transaction_id) {         
      const data = await getPersonByTransactionId(transaction_id);
      if (data) {
        await loadPerson(data);
        dispatch(setLoadingState(false));
        return;
      }
      // Fall through to error below if data not returned
    }

    // Any other explicit status (rejected/failed) OR failed retrieval => show error
    dispatch(setLoadingState(false));
    setIsGenericError(true);  
  }, [urlParams, dispatch, updateTransactionId, loadPerson, getPersonByTransactionId, step, isGenericError, setIsGenericError]);

  const onFindPlace = async () => {
    const checkLanded = ({ valid, partialAddress, addressLine3 }: { valid: boolean, partialAddress: boolean, addressLine3: string }) => {
      return valid === true && partialAddress === false && isEmpty(addressLine3);
    }
    dispatch(setLoadingState(true));
    const payload = new QASPostalCodeRequest({ postalCode: addressValues['postcode'] });
    try {
      const response = await postQASPostalAddress(payload);
      dispatch(setLoadingState(false));
      const data = get(response, 'data');
      const { valid, partialAddress, addressLine3 } = data;
      setIsFindAddressValid(valid);
      // setIsFindAddressValid(false);
      if (!valid) {
        // if (true) {
        setIsFoundAddress(false);
        patchValueAddressForm(
          {
            block: '',
            unit: '',
            street: '',
            building: '',
          }
        )
      }
      else {
        const address = new QASPostalCodeResponse(data);
        dispatch(setLoadingState(false));
        setIsFoundAddress(true);
        const isLandedProperty = checkLanded({ valid, partialAddress, addressLine3 });
        const unit = get(address, 'addressLine3', undefined);
        patchValueAddressForm(
          {
            block: get(address, 'addressLine1', ' '),
            unit: isEmpty(unit) ? undefined : unit,
            street: get(address, 'addressLine2', ' '),
            building: get(address, 'addressLine4', ' '),
            isLanded: isLandedProperty
          }
        )
      }
      trackFormSubmmit();
    } catch (error) {
      dispatch(setLoadingState(false));
      setIsFindAddressValid(false);
      setIsFoundAddress(false);
      trackFormSubmmit(get(error, 'message'));
      console.error(error);
    }
  }

  const loadRetrievedData = useCallback(() => {
    if (retrievedMyInfo) {
      const person = new MyInfoPersonResponse(retrievedMyInfo);
      const agentInformation = new AgentPersonalDetailResponse(person.personalDetails);
      const address = mapAddressInfo(person.address);
      setPersonalDetails(agentInformation);
      setAddressInformation(address);
      loadPerson(personRawResponse, false);
    }
  }, [retrievedMyInfo, loadPerson, personRawResponse]);

  useEffect(() => {
    const transaction_id: string = urlParams.get('transaction_id') || '';
    if(transaction_id) {
      dispatch(updateTransactionId(transaction_id));
    }

    if (!isPageLoaded) {
      console.log(urlParams.get('rental'));
      if (!isStep1Completed && process.env.REACT_APP_INJECT_CHECK !== 'true') {
        completeStep(false);
        onBack();
      } else {
        const paramStep = urlParams.get('step');
        trackOnload(paramStep || 0);
        if (paramStep) {
          loadRetrievedData();
          setStep(Number(paramStep));
          setIsPageLoaded(true);
        }
      }

    }
    const isInjectPerson = urlParams.get('inject');
    if (isInjectPerson === 'true' && !isInjected) {
      setInjected(true);
      console.debug('injectPersonData', injectPersonData);
      loadPerson(injectPersonData);
      return;
    } else {
      const hasConsentParams = !!(urlParams.get('status') || urlParams.get('transaction_id'));

      if (!isMyinfoLoaded && hasConsentParams) {
        handleConsent();
        setIsMyinfoLoaded(true);
      }
    }
  }, [urlParams, setStep, step, patchValue, values, dispatch, isInjected, transactionId, basicDetails, history, loadPerson, handleConsent, isPageLoaded, loadRetrievedData, isStep1Completed, onBack]);

  const addMyInfo = () => {
    const form = new AddMyInfoForm(addMyInfoValues);
    const dataPerson = { data: { person: form.person } };
    patchValue({ salutation: form.salutation })
    loadPerson(dataPerson);
    setShowMyInfoPopup(false);

  }

  return (
    <Container>
      <ContainerGrid>
        <FullGrid>
          <MultiSteps steps={5} step={2} label="Sign up using Myinfo " />
          {step === 0 && (
            <Heading margin="0 0 30px"> Personal details </Heading>
          )}
          {step === 1 && (
            <Heading margin="0 0 30px"> Personal details </Heading>
          )}
          {step === 2 && (
            <Heading margin="0 0 30px"> Residential address </Heading>
          )}
        </FullGrid>

        <LeftGrid>
          {step === 0 && (
            <React.Fragment>
              {isGenericError && (
                <InformationBox variant="error">
                  <Text>We&apos;re unable to retrieve your personal details from MyInfo to proceed with the application.</Text>
                </InformationBox>
              )}
              <Heading variant="sub" fontWeight={600}> Sign up using Myinfo </Heading>
              <Box theme={theme}>
                <Logo src={SingpassLogo} />
                <Text className="singpass-text">Sign in with your Singpass to retrieve your personal details securely. It’ll minimise the number of documents you’ll need to upload to apply for this plan.</Text>
              </Box>
              <InformationBox hasClose margin="0 0 40px">
                <Text> If you’re unable to use Myinfo, please contact us <Link text="here" variant="button" onClick={gotoContact} /></Text>
              </InformationBox>
              {isShowAddMyInfo && <React.Fragment>
                <Heading margin="0 0 5px" variant="label" >L400 profile*</Heading>
                {!errors?.l400Profile?.isValid && errors?.l400Profile?.message && (<Bullet variant="error">{errors?.l400Profile?.message}</Bullet>)}
                <Input margin="0 0 40px" name="l400Profile" type="alphanumeric" maxLength={10} value={addMyInfoValues["l400Profile"]} onChange={changeAddMyInfo}></Input>
                <Button onClick={toggleAddInfoForm} margin="0 0 20px 0" label="Add MyInfo" />
              </React.Fragment>}
              <AddInfoModal
                show={isShowMyInfoPopup}
                onClose={toggleAddInfoForm}
                onChange={changeAddMyInfo}
                valid={addMyInfoFormValid}
                values={addMyInfoValues}
                errors={addMyInfoError}
                salutations={salutations}
                nationlaities={nationlaities}
                races={races}
                maritalStatus={maritalStatus}
                onSubmit={addMyInfo}
              />
              <Button onClick={onClickRetrieveSingpass} margin="0 0 20px 0" label="Retrieve Myinfo with Singpass" />
            </React.Fragment>
          )}
          {step === 1 && (
            <form onSubmit={e => { e.preventDefault() }} noValidate>
              <InformationBox hasClose>
                <Text> Please ensure that your family name is correct. You will not be able to edit this once you proceed to the next step.</Text>
              </InformationBox>
              <Text fontWeight={600}>Full name (as per NRIC)</Text>
              <Heading fontWeight={300} variant="label" margin="0 0 30px">{`${values['fullName']}`}</Heading>
              <Heading variant="label" disabled margin="0 0 15px">Given name</Heading>
              {values['givname'] && <Input value={values['givname']} disabled readOnly margin="0 0 30px 0" />}
              <FlexRow>
                <Heading variant="label" margin="0 0 15px">Family name</Heading>
                <Link text="Swap" slotIcon="start" variant="button" onClick={onSwap} icon={IconSwap} fontWeight="bold" />
              </FlexRow>
              <Link margin="0 0 15px" text="View customer naming guidelines" fontWeight="bold" variant="button" onClick={toggleGuideline} />
              <Modal show={isShowGuideline} onClose={toggleGuideline}>
                <TableWrapper>
                  <Heading margin="0 0 30px" fontWeight="600" variant="smallTitle">Customer naming guidelines </Heading>
                  <Table source={nameGuideline.source} header={nameGuideline.header} />
                </TableWrapper>
              </Modal>
              <Input value={values['surname']} readOnly margin="0 0 30px 0" />
              <Heading variant="label" margin="0 0 15px">Salutation</Heading>
              <Dropdown source={Helpers.filterSalutations(personalDetails?.gender || '', salutations, personalDetails?.marital || '')} bindLabel="description" name="salutation" bindId="code" value={values['salutation']} onChange={handleChange} placeholder="Please select" />
              <Heading variant="label" margin="0 0 15px">Mobile number</Heading>
              {!errors?.mobileNum?.isValid && errors?.mobileNum?.message && (<Bullet variant="error">{errors?.mobileNum?.message}</Bullet>)}
              <Input variant="prefix" boxLabel="+65" name="mobileNum" invalid={!isEmpty(errors?.mobileNum?.message)} type="numeric" maxLength={8} value={values['mobileNum']} onChange={handleChange}></Input>
              <Heading variant="label" margin="0 0 15px">Email address</Heading>
              {!errors?.emailId?.isValid && errors?.emailId?.message && (<Bullet variant="error">{errors?.emailId?.message}</Bullet>)}
              <Input margin="0 0 40px" name="emailId" value={values['emailId']} invalid={!isEmpty(errors?.emailId?.message)} onChange={handleChange} maxLength={50} />
              <Button onClick={onContinue} disabled={!formValid} label="Continue" margin="0 0 20px 0" />
              <Modal show={isShowSwap} onClose={toggleSelectFamilyModal}>
                <Heading variant="smallTitle" margin="0 0 30px" fontWeight={600}>Please select your family name/last name</Heading>
                <Heading variant="label"> Family name/last name </Heading>
                {familyName.length === 0 && <Bullet variant="error">This field cannot be empty</Bullet>}
                <Input variant="tag" margin={'0 0 15px 0'} value={familyName.map((o: any) => o.w)} invalid={familyName.length === 0} onClearTag={onClearTagFamimyName} />

                <ContainerBlock>
                  {fullName.map((item: any, index: number) => (
                    <Tag disabled={!item.v} key={`${index}`} margin={'0 10px 10px 0'} callback={onSwapName(item)}>{item.w}</Tag>
                  ))}
                </ContainerBlock>
                <Button label="Confirm" margin="0 0 20px" disabled={familyName.length === 0} onClick={onConfirmSwap} />
                <Button label="Cancel" margin="0" variant="secondary" onClick={toggleSelectFamilyModal} />
              </Modal>
            </form>
          )}
          {step === 2 && (
            <form onSubmit={e => { e.preventDefault() }} noValidate>
              {addressInformation && <Listing sources={addressInformation} variant="dash" margin="0 0 30px" />}
              <Heading variant="label" margin="0 0 10px">Mailing address different from your residential address?</Heading>
              <RadioButtonGroup value={isDifferenceAddress} onChange={toggleSegment} margin={`0 0 ${!isDifferenceAddress ? '40px' : '15px'}`}>
                <RadioButton value={1} text="Yes"></RadioButton>
                <RadioButton value={0} text="No"></RadioButton>
              </RadioButtonGroup>
              {isDifferenceAddress === 1 && (
                <React.Fragment>
                  <Segment hasBorderTop={true}>
                    <Heading margin="0 0 15px" variant="label">Postcode</Heading>
                    {!addressErrors?.postcode?.isValid && addressErrors?.postcode?.message && (<Bullet variant="error">{addressErrors?.postcode?.message}</Bullet>)}
                    {!isFindAddressValid && (<Bullet variant="error">We couldn&apos;t retrieve your address using the postal code you entered, so please try again</Bullet>)}
                    <PostcodeWrapper>
                      <Input margin="0 0 15px" type="numeric" invalid={!isEmpty(addressErrors?.postcode?.message) || !isFindAddressValid}  maxLength={6} name="postcode" onChange={changeAddressForm} value={addressValues['postcode']} />
                      <Button margin="0 0 30px" label="Find my address" onClick={onFindPlace} disabled={!isEmpty(addressErrors?.postcode?.message) && !addressErrors?.postcode?.isValid}/>
                    </PostcodeWrapper>
                    

                    <Heading margin="0 0 15px" variant="label">Block / Street no.</Heading>
                    <Input margin="0 0 30px" disabled={!isFoundAddress} name="block" maxLength={30} onChange={changeAddressForm} value={addressValues['block']} />

                    <Heading margin="0 0 15px" variant="label">Unit (if any)</Heading>
                    <TextCollapsible margin="0 0 10px" labelMore="Know more" labelLess="Know less">
                      <Text> For unit number, please input # followed by numeral digits (e.g. #12-456) </Text>
                      <Text>For P.O. Box, please input numeral digits zero (eg. 0)</Text>
                      <Text margin="0 0 15px">For landed property, please input numeral digits (eg. 00-00)</Text>
                    </TextCollapsible>
                    {isFoundAddress && !addressErrors?.unit.isValid && addressErrors?.unit?.message && <Bullet variant="error">{addressErrors?.unit?.message}</Bullet>}
                    <Input margin="15px 0 30px" placeholder="e.g. #12-456" maxLength={30} invalid={isFoundAddress && !addressErrors?.unit.isValid && !isEmpty(addressErrors?.unit?.message)}  disabled={!isFoundAddress} name="unit" onChange={changeAddressForm} value={addressValues['unit']} />

                    <Heading margin="0 0 15px" variant="label">Street name</Heading>
                    <Input margin="0 0 30px" disabled={!isFoundAddress} name="street" type="alphanumeric" maxLength={30} onChange={changeAddressForm} value={addressValues['street']} />

                    <Heading margin="0 0 15px" variant="label">Building name (if any)</Heading>
                    <Input margin={0} disabled={!isFoundAddress} name="building" type="alphanumeric" maxLength={30} onChange={changeAddressForm} value={addressValues['building']} />
                  </Segment>
                  <Text margin="30px 0 40px">If the correspondence address differs from residential address, it will only apply to this policy.</Text>
                </React.Fragment>
              )}
              <Button onClick={onContinue} disabled={isDifferenceAddress === 1 && !addressFormValid} label="Continue" margin="0 0 20px 0" />
            </form>
          )}
          <Button variant="secondary" label="Back" margin="0 0 40px" onClick={onBack} />


        </LeftGrid>
        <EmptyGrid></EmptyGrid>
        <RightGrid>
          <ContactBox items={contactInformation} />
        </RightGrid>

      </ContainerGrid>
    </Container>
  );
};

export default MyInfo;
